import type {
  SeriesTemplate,
  SeriesTemplateWriteBody,
  SeriesUnit,
} from 'features/Series/api/series.types'
import { cadenceText } from 'features/Series/pages/shared/cadenceText'
import { SERIES_PAGES_TEXT } from 'features/Series/pages/shared/seriesPagesText'

const NAME_MAX = 150
const DESCRIPTION_MAX = 1000
const ISSUES_MAX = 20
const DURATION_STEP = 5
const DURATION_MAX = 995

export type TemplateIssue = { type: string; code: string; description: string }

export type SeriesTemplateDraft = {
  name: string
  description: string
  visits: number
  every: number
  unit: SeriesUnit
  duration: number
  categoryId: number | null
  billingCode: { type: string; code: string } | null
  issues: TemplateIssue[]
  anyFacility: boolean
  active: boolean
}

export const BLANK_DRAFT: SeriesTemplateDraft = {
  name: '',
  description: '',
  visits: 6,
  every: 1,
  unit: 'week',
  duration: 30,
  categoryId: null,
  billingCode: null,
  issues: [],
  anyFacility: true,
  active: true,
}

export const draftFromTemplate = (t: SeriesTemplate): SeriesTemplateDraft => ({
  name: t.name,
  description: t.description ?? '',
  visits: t.visits,
  every: t.every,
  unit: t.unit,
  duration: t.duration,
  categoryId: t.category?.id ?? null,
  billingCode: t.billingCode ? { type: t.billingCode.type, code: t.billingCode.code } : null,
  issues: t.issueCodes.map((c) => ({
    type: c.type,
    code: c.code,
    description: c.description ?? '',
  })),
  anyFacility: t.facility === null,
  active: t.active,
})

export const toWriteBody = (d: SeriesTemplateDraft): SeriesTemplateWriteBody => ({
  name: d.name.trim().slice(0, NAME_MAX),
  description: d.description.trim().slice(0, DESCRIPTION_MAX),
  visits: d.visits,
  every: d.every,
  unit: d.unit,
  duration: d.duration,
  category_id: d.categoryId,
  billing_code: d.billingCode ? { type: d.billingCode.type, code: d.billingCode.code } : null,
  issue_codes: [...new Set(d.issues.map((i) => `${i.type}:${i.code}`))].slice(0, ISSUES_MAX),
  any_facility: d.anyFacility,
  active: d.active,
})

export const saveAsNewName = (draftName: string, originalName: string): string => {
  const name = draftName.trim()
  return name === originalName.trim()
    ? SERIES_PAGES_TEXT.modal.newName(name)
    : name.slice(0, NAME_MAX)
}

export const duplicateName = (name: string): string => `Copy of ${name}`.slice(0, NAME_MAX)

// Nearest multiple of 5, kept inside 5..995.
export const snapDuration = (n: number): number =>
  Math.min(DURATION_MAX, Math.max(DURATION_STEP, Math.round(n / DURATION_STEP) * DURATION_STEP))

export type TemplateField =
  | 'name'
  | 'description'
  | 'visits'
  | 'every'
  | 'unit'
  | 'duration'
  | 'category_id'
  | 'billing_code'
  | 'issue_codes'
  | 'any_facility'

const FIELDS: readonly string[] = [
  'name',
  'description',
  'visits',
  'every',
  'unit',
  'duration',
  'category_id',
  'billing_code',
  'issue_codes',
  'any_facility',
]

const isTemplateField = (key: string): key is TemplateField => FIELDS.includes(key)

export const templateFieldErrors = (
  errors: Record<string, string[]> = {},
): { fields: Partial<Record<TemplateField, string>>; other: string[] } =>
  Object.entries(errors).reduce(
    (acc, [key, messages]) => {
      const field = key.split('.')[0]
      if (isTemplateField(field)) {
        return acc.fields[field] === undefined
          ? { ...acc, fields: { ...acc.fields, [field]: messages[0] } }
          : acc
      }
      return { ...acc, other: [...acc.other, ...messages] }
    },
    { fields: {} as Partial<Record<TemplateField, string>>, other: [] as string[] },
  )

export const templateFooterNote = (d: SeriesTemplateDraft): string =>
  SERIES_PAGES_TEXT.modal.footerNote(d.visits, cadenceText(d.every, d.unit), d.duration)
